/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  SLRLabelIcon,
  SLRMediaItem,
  getIcon,
  useGetOrganizationKey,
} from "@slr/shared-library";
import { Organization, OrganizationType } from "@slr/user-service-external-sdk";
import {
  DEFAULT_ORGANIZATION_IMAGE_PATH,
  ENCRYPTION_ENABLED,
  PROFILE_ORGANIZATIONS_PATH,
} from "configs";
import { useQueryParam } from "providers";
import { FC } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { OrganizationDropdown } from "./OrganizationDropdown";

import "./OrganizationHeader.scss";

type OrganizationHeaderProps = {
  organization: Organization;
  isMember?: boolean;
  isAdmin?: boolean;
  onEdit: () => void;
  onManageKey: () => void;
  onDelete: () => void;
  onLeave: () => void;
};

export const OrganizationHeader: FC<OrganizationHeaderProps> = ({
  organization,
  isMember,
  isAdmin,
  onManageKey,
  onEdit,
  onLeave,
  onDelete,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationsPage.details",
  });

  const { data: organizationKey } = useGetOrganizationKey(
    ENCRYPTION_ENABLED ? organization.id : ""
  );
  const { solutionBarQuery } = useQueryParam();
  const { showEncryption } = solutionBarQuery || {};

  const dropDown = (
    <OrganizationDropdown
      isAdmin={isAdmin}
      isMember={isMember}
      isUserOrganization={organization?.type === OrganizationType.User}
      showEncryption={Boolean(
        ENCRYPTION_ENABLED &&
        ((isAdmin && organizationKey?.key) || showEncryption)
      )}
      onManageKey={onManageKey}
      onEdit={onEdit}
      onDelete={onDelete}
      onLeave={onLeave}
    />
  );

  return (
    <Container className="organization-details">
      <Row className="py-2 justify-content-between align-items-center organization-header">
        <Col
          xs="12"
          md="4"
          lg="3"
          className="d-flex align-items-center justify-content-between mb-2 mb-md-0"
        >
          <Link
            to={PROFILE_ORGANIZATIONS_PATH}
            title={t("navigateToOverview")}
            className="btn btn-link"
          >
            <FontAwesomeIcon icon={getIcon("fas", "chevron-left")} size="3x" />
          </Link>
          <SLRMediaItem
            mediaItemId={organization.logoId}
            altFallback={t("organizationLogoAlt", {
              organization: organization.name,
            })}
            errorFallbackImageSource={DEFAULT_ORGANIZATION_IMAGE_PATH}
            roundedCircle
            className="logo"
          />
          <div className="d-block d-md-none">{dropDown}</div>
        </Col>

        <Col xs="12" md="7" lg="8">
          <Row className="name fs-3 text-center text-md-start">
            <Col>{organization?.name}</Col>
          </Row>

          {(organization?.websiteUrl ||
            organization?.email ||
            organization?.phoneNumber ||
            organization?.address) && (
              <Row className="pt-2 gap-2 tags">
                {organization?.websiteUrl && (
                  <SLRLabelIcon
                    label={organization?.websiteUrl}
                    link={organization?.websiteUrl}
                    target={"_blank"}
                    icon={getIcon("fas", "globe")}
                  />
                )}
                {organization?.email && (
                  <SLRLabelIcon
                    label={organization?.email}
                    link={`mailto:${organization?.email}`}
                    icon={getIcon("fas", "envelope")}
                  />
                )}
                {organization?.phoneNumber && (
                  <SLRLabelIcon
                    label={organization?.phoneNumber}
                    link={`tel:${organization?.phoneNumber}`}
                    icon={getIcon("fas", "phone")}
                  />
                )}
                {organization?.address && (
                  <SLRLabelIcon
                    label={organization?.address}
                    icon={getIcon("fas", "location-dot")}
                  />
                )}
              </Row>
            )}
        </Col>

        <Col xs="auto" md="1" className="text-end d-none d-md-block">
          {dropDown}
        </Col>
      </Row>
    </Container>
  );
};
